import React from 'react';
import { Box, Typography } from '@mui/material';

interface LogoProps {
  mode?: 'full' | 'icon';
  size?: number;
  textColor?: string;
  subtitle?: string;
}

export const LogoIcon: React.FC<{ size?: number }> = ({ size = 32 }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ overflow: 'visible' }}
    >
      <defs>
        {/* Glowing gradients */}
        <linearGradient id="globeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4fc3f7" />
          <stop offset="50%" stopColor="#0284c7" />
          <stop offset="100%" stopColor="#0b3c5d" />
        </linearGradient>

        <linearGradient id="pinGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="30%" stopColor="#38bdf8" />
          <stop offset="100%" stopColor="#0369a1" />
        </linearGradient>

        <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#4fc3f7" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#38bdf8" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#0284c7" stopOpacity="0.2" />
        </linearGradient>

        <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* Background soft glow ring */}
      <circle
        cx="50"
        cy="50"
        r="44"
        stroke="url(#ringGrad)"
        strokeWidth="1.5"
        strokeDasharray="4 4"
        fill="transparent"
      />

      {/* Main Globe Base */}
      <circle
        cx="50"
        cy="50"
        r="38"
        stroke="url(#globeGrad)"
        strokeWidth="2.5"
        fill="rgba(11, 23, 42, 0.65)"
        style={{ filter: 'drop-shadow(0px 4px 12px rgba(2, 132, 199, 0.25))' }}
      />

      {/* Latitudinal grid lines */}
      <path d="M15 50 H85" stroke="rgba(79, 195, 247, 0.35)" strokeWidth="1.5" />
      <path d="M20.5 35 Q50 42 79.5 35" stroke="rgba(79, 195, 247, 0.2)" strokeWidth="1.25" />
      <path d="M20.5 65 Q50 58 79.5 65" stroke="rgba(79, 195, 247, 0.2)" strokeWidth="1.25" />

      {/* Longitudinal grid lines */}
      <path d="M50 12 V88" stroke="rgba(79, 195, 247, 0.35)" strokeWidth="1.5" />
      <path d="M32 17 Q45 50 32 83" stroke="rgba(79, 195, 247, 0.25)" strokeWidth="1.25" />
      <path d="M68 17 Q55 50 68 83" stroke="rgba(79, 195, 247, 0.25)" strokeWidth="1.25" />

      {/* Stylized Active GIS node network (linked points) */}
      <circle cx="32" cy="35" r="3" fill="#4fc3f7" filter="url(#glow)" />
      <circle cx="68" cy="65" r="3" fill="#38bdf8" />
      <circle cx="28" cy="58" r="2.5" fill="#0284c7" />

      {/* Network connection line */}
      <path
        d="M32 35 L50 42 L68 65"
        stroke="rgba(56, 189, 248, 0.45)"
        strokeWidth="1"
        strokeDasharray="2 2"
      />

      {/* Glowing Spatial Double-Layered Pin in center */}
      <g filter="url(#glow)">
        {/* Outer neon diamond */}
        <path
          d="M50 24 L64 42 L50 60 L36 42 Z"
          fill="rgba(56, 189, 248, 0.25)"
          stroke="#4fc3f7"
          strokeWidth="1.5"
        />
        {/* Inner solid brilliant diamond core */}
        <path
          d="M50 29 L59 42 L50 55 L41 42 Z"
          fill="url(#pinGrad)"
          style={{ filter: 'drop-shadow(0px 2px 6px rgba(255, 255, 255, 0.5))' }}
        />
        {/* Center shining white vertex */}
        <circle cx="50" cy="42" r="2" fill="#ffffff" />
      </g>
    </svg>
  );
};

const Logo: React.FC<LogoProps> = ({
  mode = 'full',
  size = 32,
  textColor = '#ffffff',
  subtitle,
}) => {
  if (mode === 'icon') {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: 'rgba(15, 23, 42, 0.8)',
          border: '1.5px solid rgba(79, 195, 247, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 12px rgba(79, 195, 247, 0.25)',
          transition: 'transform 0.3s ease',
          '&:hover': {
            transform: 'scale(1.08)',
            borderColor: '#4fc3f7',
            boxShadow: '0 0 16px rgba(79, 195, 247, 0.45)',
          },
        }}
      >
        <LogoIcon size={size * 0.72} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: 'rgba(15, 23, 42, 0.6)',
          borderRadius: '10px',
          p: 0.65,
          border: '1px solid rgba(79, 195, 247, 0.2)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }}
      >
        <LogoIcon size={size} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          lineHeight: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          variant="h6"
          noWrap
          sx={{
            fontWeight: 900,
            fontSize: size * 0.48,
            letterSpacing: '0.4px',
            color: textColor,
            lineHeight: 1.1,
            background: `linear-gradient(135deg, ${textColor} 30%, #4fc3f7 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          GeoVera
        </Typography>
        <Typography
          variant="caption"
          noWrap
          sx={{
            fontSize: size * 0.28,
            color: 'rgba(232, 244, 252, 0.55)',
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            fontWeight: 700,
            lineHeight: 1,
            mt: 0.25,
          }}
        >
          {subtitle || 'WebGIS Platform'}
        </Typography>
      </Box>
    </Box>
  );
};

export default Logo;
